import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Patient, Treatment, Session } from "@akina/domain";
import { useDetectNetworkEffectiveType, useGetDeviceInfo, useGetOSInfo } from "@akina/presentation";
import {
  type PropertyTypeMap,
  type SessionPropertyTypeMap,
  AmplitudeProperties,
  type ScreenStateType,
  SessionAmplitudeProperties,
  SessionScreenTypes,
} from "./";

export const calculateScreenDuration = (startTime: number | null) => {
  if (startTime) return (Date.now() - startTime) / 1000;
  return null;
};

export const getScreenDurationProperty = (screenStartTime: number | null) => ({
  [SessionAmplitudeProperties.ScreenDurationAtExit]: calculateScreenDuration(screenStartTime),
});

export const addScreenTime = (analyticsData: SessionPropertyTypeMap) => {
  const startTime = analyticsData[SessionAmplitudeProperties.ScreenDurationAtExit];
  return {
    ...analyticsData,
    ...getScreenDurationProperty(startTime),
  };
};

export const addCameraSwitchData = (webcamIsFullScreen: boolean, screenType: SessionScreenTypes) => {
  const screensToTrack = [SessionScreenTypes.RoutineScreen, SessionScreenTypes.BreakScreen];
  if (!screensToTrack.includes(screenType)) return {};
  return {
    [SessionAmplitudeProperties.ScreenState]: webcamIsFullScreen
      ? "camera_in_center"
      : ("coach_in_center" as ScreenStateType),
  };
};

export const useTrackSessionEventInfo = ({
  screenType,
  screenIndex,
  session,
}: {
  screenType: string;
  screenIndex: number;
  session?: Session | null;
}) => {
  const [screenDurationAtExit, setScreenDurationAtExit] = useState<number | null>(new Date().getTime()); // NOSONAR

  const [data, setData] = useState<SessionPropertyTypeMap>({
    [SessionAmplitudeProperties.CurrentPhaseWeek]: session?.week ?? 0,
    [SessionAmplitudeProperties.ScreenType]: screenType,
    [SessionAmplitudeProperties.ScreenIndex]: screenIndex,
    [SessionAmplitudeProperties.IsInitialSession]: session ? session?.currentNumber == 1 : true,
    [SessionAmplitudeProperties.ScreenDurationAtExit]: new Date().getTime(), // NOSONAR
    [SessionAmplitudeProperties.SessionLogId]: session?.id ?? "",
    [SessionAmplitudeProperties.SessionLogIndex]: session?.currentNumber ?? 0,
    [SessionAmplitudeProperties.ScreenState]: "coach_in_center",
  });

  useEffect(() => {
    setData(prevData => ({
      ...prevData,
      [SessionAmplitudeProperties.ScreenDurationAtExit]: screenDurationAtExit,
    }));
  }, [screenDurationAtExit]);

  const resetScreenDurationAtExit = () => {
    setScreenDurationAtExit(Date.now());
  };

  const addSessionData = (session: Session | undefined, analyticsData: SessionPropertyTypeMap) => ({
    ...analyticsData,
    [SessionAmplitudeProperties.CurrentPhaseWeek]: session?.week ?? 0,
    [SessionAmplitudeProperties.IsInitialSession]: session?.currentNumber == 1,
    [SessionAmplitudeProperties.SessionLogId]: session?.id ?? "",
    [SessionAmplitudeProperties.SessionLogIndex]: session?.currentNumber ?? 0,
  });

  return { data, addSessionData, resetScreenDurationAtExit };
};

export const useTrackCareEventInfo = ({ pageName }: { pageName: string }) => {
  const { pathname } = useLocation();
  const { parsedOsInfo } = useGetOSInfo();
  const { parsedDeviceInfo } = useGetDeviceInfo();
  const { currentConnectionType } = useDetectNetworkEffectiveType();

  const [data, setData] = useState<PropertyTypeMap>({
    [AmplitudeProperties.Timestamp]: new Date().getTime(), // NOSONAR
    [AmplitudeProperties.UserId]: "",
    [AmplitudeProperties.Language]: "",
    [AmplitudeProperties.BrowserInformation]: navigator.userAgent,
    [AmplitudeProperties.App]: "akina-care",
    [AmplitudeProperties.Height]: window.innerHeight,
    [AmplitudeProperties.Width]: window.innerWidth,
    [AmplitudeProperties.PageName]: pageName,
    [AmplitudeProperties.Path]: pathname,
    [AmplitudeProperties.TreatmentId]: "",
    [AmplitudeProperties.TreatmentTemplateKey]: "",
    [AmplitudeProperties.OperatingSystemInformation]: parsedOsInfo,
    [AmplitudeProperties.DeviceInformation]: parsedDeviceInfo,
    [AmplitudeProperties.ScreenResolution]: `${window.innerWidth}x${window.innerHeight}`,
    [AmplitudeProperties.NetworkEffectiveType]: currentConnectionType ?? "unknown",
    [AmplitudeProperties.OrganizationId]: "",
  });

  useEffect(() => {
    setData(prevData => ({
      ...prevData,
      [AmplitudeProperties.ScreenResolution]: `${window.innerWidth}x${window.innerHeight}`,
      [AmplitudeProperties.NetworkEffectiveType]: currentConnectionType ?? "unknown",
      [AmplitudeProperties.OperatingSystemInformation]: parsedOsInfo,
      [AmplitudeProperties.DeviceInformation]: parsedDeviceInfo,
      [AmplitudeProperties.Height]: window.innerHeight,
      [AmplitudeProperties.Width]: window.innerWidth,
    }));
  }, [currentConnectionType, parsedOsInfo, parsedDeviceInfo]);

  const addPatientAndTreatmentData = (
    patient: Patient | undefined,
    treatment: Treatment | undefined | null,
    analyticsData: PropertyTypeMap,
  ) => {
    return {
      ...analyticsData,
      [AmplitudeProperties.OrganizationId]: treatment?.clinicId ?? "",
      [AmplitudeProperties.UserId]: patient?.patientId || "",
      [AmplitudeProperties.Language]: patient?.preferences.language || "",
      [AmplitudeProperties.TreatmentId]: treatment?.id || patient?.treatmentIds[0] || "",
      [AmplitudeProperties.TreatmentTemplateKey]: treatment?.treatmentTemplateKey || "",
    };
  };

  return { data, addPatientAndTreatmentData };
};
